import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { Navbar } from './Navbar';
import { LeftSidebar } from './LeftSidebar';
import { RightSidebar } from './RightSidebar';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/hooks/useSettings';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

export const Layout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { uiSettings, isLoaded, saveUISettings } = useSettings();
  const { isAuthenticated, isLoading, accessToken } = useAuth();
  const location = useLocation();
  
  // Initialize with default values first
  const [leftMenuOpen, setLeftMenuOpen] = React.useState(false);
  const [rightMenuOpen, setRightMenuOpen] = React.useState(false);
  const [hasSecondLevelMenu, setHasSecondLevelMenu] = React.useState(false);
  const [hasThirdLevelMenu, setHasThirdLevelMenu] = React.useState(false);
  const [compactMode, setCompactMode] = React.useState(false);
  const [compactSecondLevel, setCompactSecondLevel] = React.useState(false);

  // Show loading spinner while auth is initializing
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Sync with settings when they load and change
  React.useEffect(() => {
    if (isLoaded) {
      setLeftMenuOpen(uiSettings.leftSidebarOpen);
      setRightMenuOpen(uiSettings.rightSidebarOpen);
    }
  }, [uiSettings.leftSidebarOpen, uiSettings.rightSidebarOpen, isLoaded]);

  // Check if we need second and third level menus based on current route
  React.useEffect(() => {
    const path = location.pathname;
    
    // Define sections that have subsections (second level)
    const sectionsWithSubsections = [
      '/notifications',
      '/teams',
      '/balance',
      // Add more sections as needed
    ];
    
    // Show second level menu if we're in a section with subsections AND not on the main section page
    const hasSecondLevel = sectionsWithSubsections.some(section => {
      return path.startsWith(section) && path !== section;
    });
    
    // Show third level menu if we're viewing a specific team
    const hasThirdLevel = path.match(/^\/teams\/[^/]+$/); // matches /teams/{teamId}
    
    // Enable compact mode when showing second level (any subsections) or third level (team page)
    const shouldUseCompactMode = hasSecondLevel || !!hasThirdLevel;
    
    setHasSecondLevelMenu(hasSecondLevel);
    setHasThirdLevelMenu(!!hasThirdLevel);
    setCompactMode(shouldUseCompactMode);
  }, [location.pathname]);

  // On mobile, auto-hide the left menu when entering the player pages so the
  // game gallery / cabinet gets the full width.
  React.useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (isMobile && location.pathname.startsWith('/play')) {
      setLeftMenuOpen(false);
    }
  }, [location.pathname]);

  // Calculate left margin based on active menus and compact mode
  const calculateLeftMargin = () => {
    if (!leftMenuOpen) return '0px';
    
    let margin = compactMode ? 60 : 250; // Main menu width
    
    if (hasSecondLevelMenu) {
      margin += compactSecondLevel ? 60 : 200; // Second level menu width
    }
    
    if (hasThirdLevelMenu) {
      margin += 200; // Third level menu width (team navigation)
    }
    
    return `${margin}px`;
  };

  const leftMargin = calculateLeftMargin();
  const rightMargin = rightMenuOpen ? '300px' : '0px';

  return (
    <Flex direction="column" minH="100vh">
      {/* Navbar */}
      <Navbar
        onLeftMenuToggle={() => {
          const newState = !leftMenuOpen;
          setLeftMenuOpen(newState);
          saveUISettings({ leftSidebarOpen: newState });
        }}
        onRightMenuToggle={() => {
          const newState = !rightMenuOpen;
          setRightMenuOpen(newState);
          saveUISettings({ rightSidebarOpen: newState });
        }}
        isLeftMenuOpen={leftMenuOpen}
        isRightMenuOpen={rightMenuOpen}
      />

      {/* Main layout with sidebars */}
      <Flex flex={1} position="relative">
        {/* Left Sidebar */}
        <LeftSidebar isOpen={leftMenuOpen} />

        {/* Right Sidebar */}
        <RightSidebar isOpen={rightMenuOpen} />

        {/* Main content area */}
        <Box
          flex={1}
          ml={leftMargin}
          mr={rightMargin}
          transition="margin 0.3s ease-in-out"
          minH="calc(100vh - 60px)"
          position="relative"
        >
          {children || <Outlet />}
        </Box>
      </Flex>
    </Flex>
  );
}; 